import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container, SectionTitle } from '../../ui';
import styles from './IndustriesMatrix.module.css';

interface MatrixData {
  sectors: string[];
  rows: { label: string; values: string[] }[];
}

export function IndustriesMatrix() {
  const { t } = useTranslation('solutions');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const data = t('industriesMatrix.data', { returnObjects: true }) as MatrixData;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section variant="gray">
      <Container>
        <SectionTitle
          title={t('industriesMatrix.title')}
          subtitle={t('industriesMatrix.subtitle')}
          centered
        />
        <div ref={ref} className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}>
          {/* Desktop table */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.cornerCell} />
                  {data.sectors.map((sector) => (
                    <th key={sector} className={styles.headerCell}>{sector}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr
                    key={row.label}
                    className={styles.row}
                    style={{ '--row-index': i } as React.CSSProperties}
                  >
                    <td className={styles.labelCell}>{row.label}</td>
                    {row.values.map((val, j) => (
                      <td key={j} className={styles.valueCell}>{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className={styles.mobileCards}>
            {data.sectors.map((sector, si) => (
              <div
                key={sector}
                className={styles.mobileCard}
                style={{ '--card-index': si } as React.CSSProperties}
              >
                <h3 className={styles.mobileCardTitle}>{sector}</h3>
                <dl className={styles.dl}>
                  {data.rows.map((row) => (
                    <div key={row.label} className={styles.dlRow}>
                      <dt className={styles.dt}>{row.label}</dt>
                      <dd className={styles.dd}>{row.values[si]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
